import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Images } from "../../../../utils/imageConstants";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { QUERY_KEY } from "../../../../services/queryEndPoint";
import { Api } from "../../../../services/interceptor";
import BreadCrumb from "../../../../components/BreadCrumb";
import Calendar from "../../../../components/Calendar";
import type { MatchTab } from "../../tournaments/tournamentsDetails/matches/types/IMatches";
import type { IMatchesResponse } from "../../types/home";
import LiveMatches from "../../tournaments/tournamentsDetails/matches/LiveMatches";
import UpcomingMatches from "../../tournaments/tournamentsDetails/matches/UpcomingMatches";
import PastMatches from "../../tournaments/tournamentsDetails/matches/PastMatches";
import EditMatchScheduleModal from "../../tournaments/tournamentsDetails/matches/EditMatchScheduleModal";
import { useAppSelector } from "../../../../redux/hooks";
import { selectSelectedLeague } from "../../../../redux/slice/selectedLeagueSlice";
import { getAuthToken, getGuestModeFromStorage } from "../../../../helper/helper";
import { canScheduleMatchesInLeague } from "../../../../utils/leagueAccess";
import {
  calendarYearsFromMatches,
  filterMatchesByDateRange,
  filterMatchesBySearchQuery,
  formatMatchesDateRangeLabel,
  startOfDay,
} from "../../tournaments/tournamentsDetails/matches/matchDateFilterUtils";

const MATCH_PAGE_SIZE = 6;
const MATCHES_MOBILE_MAX_PX = 768;

const TeamMatches: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [viewList, setViewList] = useState(false);
  const [matchLive, setMatchLive] = useState<MatchTab>("live");
  const [open, setOpen] = useState(false);
  const [appliedDateRange, setAppliedDateRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [matchSearchQuery, setMatchSearchQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const tournamentInfo = location.pathname.includes("/tournaments/team-info");
  const tournamentId = sessionStorage.getItem("tournamentId");
  const tournamentName = sessionStorage.getItem("tournamentName");
  const teamSnapshotId = sessionStorage.getItem("teamSnapshotId");
  const token = getAuthToken();
  const isGuestModeRedux = useAppSelector((s) => s.authModal.isGuestMode);
  const isGuestModeStorage = getGuestModeFromStorage();
  const isGuestMode = isGuestModeRedux || isGuestModeStorage;
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const canScheduleMatches = canScheduleMatchesInLeague(selectedLeague);
  const [editScheduleMatchId, setEditScheduleMatchId] = useState<string | null>(null);
  const [isNarrowMatches, setIsNarrowMatches] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia(`(max-width: ${MATCHES_MOBILE_MAX_PX}px)`).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MATCHES_MOBILE_MAX_PX}px)`);
    const sync = () => setIsNarrowMatches(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const openEditMatch = useCallback((id: string) => {
    const t = typeof id === "string" ? id.trim() : "";
    if (t) setEditScheduleMatchId(t);
  }, []);

  const closeEditMatch = useCallback(() => setEditScheduleMatchId(null), []);

  const TABS: Array<{
    key: MatchTab;
    label: string;
  }> = [
      {
        key: "live",
        label: "LIVE",
      },
      {
        key: "upcoming",
        label: "UPCOMING",
      },
      {
        key: "past",
        label: "PAST",
      },
    ];

  const effectiveViewList = isNarrowMatches ? false : viewList;

  const matchesQueryEnabled = tournamentInfo
    ? Boolean(tournamentId && teamSnapshotId)
    : Boolean(tournamentId);

  const {
    data: infiniteData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [
      tournamentInfo
        ? QUERY_KEY.matchByTeamAndStatus
        : QUERY_KEY.matchByTournamentIdAndStatusPageable,
      matchLive,
      tournamentId,
      teamSnapshotId,
    ],
    enabled: matchesQueryEnabled,
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const endpoint = tournamentInfo
        ? QUERY_KEY.matchByTeamAndStatus
        : QUERY_KEY.matchByTournamentIdAndStatusPageable;
      const params: Record<string, string | number> = {
        status:
          matchLive === "live"
            ? "IN_PROGRESS"
            : matchLive === "upcoming"
              ? "SCHEDULED"
              : "ENDED",
        page: pageParam,
        size: MATCH_PAGE_SIZE,
      };
      if (tournamentInfo) {
        params.teamId = teamSnapshotId ?? "";
      } else {
        params.tournamentId = tournamentId ?? "";
      }
      const response = await Api.get<IMatchesResponse>(endpoint, { params });
      return response?.data ?? { matchResponseList: [] };
    },
    getNextPageParam: (lastPage, allPages) => {
      const list = lastPage?.matchResponseList ?? [];
      if (list.length < MATCH_PAGE_SIZE) return undefined;
      return allPages.length;
    },
  });

  const matches = useMemo(
    () =>
      infiniteData?.pages.flatMap((p) => p.matchResponseList ?? []) ?? [],
    [infiniteData],
  );

  useEffect(() => {
    setAppliedDateRange(null);
    setMatchSearchQuery("");
  }, [matchLive]);

  const filteredMatches = useMemo(() => {
    const byDate = filterMatchesByDateRange(matches, appliedDateRange);
    return filterMatchesBySearchQuery(byDate, matchSearchQuery);
  }, [matches, appliedDateRange, matchSearchQuery]);

  const dateRangeButtonLabel = useMemo(
    () => formatMatchesDateRangeLabel(appliedDateRange, matches),
    [appliedDateRange, matches],
  );

  const calendarYears = useMemo(() => calendarYearsFromMatches(matches), [matches]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (
        open &&
        wrapRef.current &&
        !wrapRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <EditMatchScheduleModal
        open={editScheduleMatchId !== null}
        onOpenChange={(v) => {
          if (!v) closeEditMatch();
        }}
        matchId={editScheduleMatchId}
        tournamentId={tournamentId}
        leagueId={selectedLeague?.leagueId ?? null}
        matchType={sessionStorage.getItem("tournamentMatchType") ?? ""}
        tournamentCategory={sessionStorage.getItem("tournamentCategory") ?? ""}
      />
      <div className={!tournamentInfo ? "main-match-wrapper" : ""}>
        <div className={!tournamentInfo ? "custom-container main-middle-content-wrap" : ""}>
          {!tournamentInfo &&
            <BreadCrumb />
          }
          <div className={!tournamentInfo ? "match-bg-wrapper" : ""}>
            {!tournamentInfo &&
              <div className="images-name-wrapper">
                <p className="match-name">
                  {tournamentName || "-"}
                </p>
              </div>
            }
            <div className={!tournamentInfo ? "matches-wrapper-container" : "team-info-matches-wrapper-container"}>
              <div
                className={`matches-header${isNarrowMatches ? " matches-header--narrow" : ""}`}
              >
                {!tournamentInfo &&
                  <div className="matches-main-header">
                    {/* <h1 className="heading">Matches</h1> */}

                  </div>
                }
                <div className="toolbar">
                  <div className="tabs" role="tablist" aria-label="Match Tabs">
                    {TABS.map(({ key, label }) => {
                      const isActive = matchLive === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          onClick={() => setMatchLive(key)}
                          className={`chip ${isActive ? "active" : ""}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="right-filters-data">
                    <div className="controls">
                      <div className="search">
                        <input
                          type="search"
                          placeholder="Search Matches..."
                          value={matchSearchQuery}
                          onChange={(e) => setMatchSearchQuery(e.target.value)}
                          aria-label="Search matches by team, venue, or tournament"
                          autoComplete="off"
                        />
                      </div>
                      {(token && !isGuestMode && canScheduleMatches) && (
                        <div className="button-wrap">
                          <button
                            type="button"
                            className="schdule-button"
                            onClick={() => navigate("/tournaments/schedule-match")}
                          >
                            <img src={Images.schedule_icon} alt="" />
                            <span>Schedule</span>
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="date-calendar" ref={wrapRef}>
                      <button
                        className="date-calendar-filter"
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        aria-expanded={open}
                        aria-haspopup="dialog"
                      >
                        <span>{dateRangeButtonLabel}</span>
                        <img src={Images.filter_dropdown_icon} alt="" />
                      </button>

                      {open && (
                        <div
                          className="calendar-popover"
                          role="dialog"
                          aria-label="Choose date range"
                        >
                          <Calendar
                            key={`${appliedDateRange?.start?.getTime() ?? "x"}-${appliedDateRange?.end?.getTime() ?? "y"}`}
                            initialStartDate={appliedDateRange?.start ?? null}
                            initialEndDate={appliedDateRange?.end ?? null}
                            years={calendarYears}
                            onApply={(range) => {
                              const s = range.startDate;
                              const e = range.endDate ?? range.startDate;
                              if (!s && !e) {
                                setAppliedDateRange(null);
                              } else if (s) {
                                setAppliedDateRange({
                                  start: startOfDay(s),
                                  end: startOfDay(e ?? s),
                                });
                              }
                              setOpen(false);
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="filter-view-icons">
                      <div className="matches-view-switch" role="group" aria-label="View mode">
                        <button
                          type="button"
                          className={`matches-view-toggle ${!viewList ? "active" : ""}`}
                          onClick={() => setViewList(false)}
                          aria-label="Grid view"
                          aria-pressed={!viewList}
                        >
                          <img src={Images.card_show_icon} alt="" />
                        </button>
                        <button
                          type="button"
                          className={`matches-view-toggle ${viewList ? "active" : ""}`}
                          onClick={() => setViewList(true)}
                          aria-label="List view"
                          aria-pressed={viewList}
                        >
                          <img src={Images.list_view_icon} alt="" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {matchLive === "live" && (
                <LiveMatches
                  matches={filteredMatches}
                  isLoading={isLoading}
                  viewList={effectiveViewList}
                />
              )}
              {matchLive === "upcoming" && (
                <UpcomingMatches
                  matches={filteredMatches}
                  isLoading={isLoading}
                  viewList={effectiveViewList}
                  canEditMatch={Boolean(token && !isGuestMode && canScheduleMatches)}
                  onEditMatch={
                    token && !isGuestMode && canScheduleMatches ? openEditMatch : undefined
                  }
                />
              )}
              {matchLive === "past" && (
                <PastMatches
                  matches={filteredMatches}
                  isLoading={isLoading}
                  viewList={effectiveViewList}
                />
              )}
              {matchesQueryEnabled && hasNextPage && !isLoading && (
                <div className="matches-load-more-wrap">
                  <button
                    type="button"
                    className="matches-load-more-btn"
                    disabled={isFetchingNextPage}
                    onClick={() => void fetchNextPage()}
                  >
                    {isFetchingNextPage ? "Loading…" : "Show more"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeamMatches;
